import {useState, useContext} from 'react';
import {Form} from 'react-bootstrap';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
} from 'firebase/auth';
import {LoginContext, LoginScreen} from './LoginContext';
import {LogInLinkButton, PrimaryLogInButton} from './SignInButton';
import {AUTH_TEST_IDS} from './authTestIds';
import {useTranslateFunction} from 'contexts/translation/TranslationContext';
import {logEvent} from 'firebase/analytics';
import {getOptionalAnalytics} from 'firebaseApp';
import {carryReferralCode} from '@cip/core/application/referrals';
import {isBrowserOffline} from 'utils/connectionMessages';
import {errorCodeOf, signInFailureAdvice} from './emailSendError';
import {SignInAlert} from './SignInAlert';

export const EmailPasswordForm = ({auth, showSignIn}) => {
  const [, setLoginState] = useContext(LoginContext);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordMatch, setPasswordMatch] = useState('');
  const [error, setError] = useState('');
  const [connectionAdvice, setConnectionAdvice] = useState<string | null>(null);

  const t = useTranslateFunction('miscUI');
  // Carried for the same reason as in Login: the verification link may open
  // in another browser, where only the URL can bring the referral code along.
  const url = carryReferralCode({
    link: window.location.origin + window.location.pathname,
    search: window.location.search,
  });
  const errorMessages = [
    'invalid-password',
    'auth/invalid-email',
    'auth/user-not-found',
    'auth/wrong-password',
    'auth/missing-password',
    'auth/invalid-credential',
  ];
  const actionCodeSettings = {
    url: url,
    handleCodeInApp: true,
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setConnectionAdvice(null);
    try {
      if (showSignIn) {
        await signInWithEmailAndPassword(auth, email, password);
        setLoginState(LoginScreen.UNVERIFIED);
      } else {
        //sign up page
        if (password.length < 8) {
          throw new Error('invalid-password');
        }
        if (password != passwordMatch) {
          throw new Error('no-match');
        }
        const userCredential = await createUserWithEmailAndPassword(
          auth,
          email,
          password,
        );
        const analytics = getOptionalAnalytics();
        if (analytics) {
          logEvent(analytics, 'sign_up', {method: 'email'});
        }
        await sendEmailVerification(userCredential.user, actionCodeSettings);
        setLoginState(LoginScreen.UNVERIFIED);
      }
    } catch (error) {
      setError(error.code ?? error.message);
      // Only Firebase's own failures carry a code; the local password checks
      // above never touch the network.
      const code = errorCodeOf(error);
      const advice =
        code === undefined
          ? undefined
          : signInFailureAdvice({code, isOffline: isBrowserOffline()});
      setConnectionAdvice(
        advice === undefined ? null : t(advice, {forceString: true}),
      );
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Form.Group className="mb-3" controlId="formBasicEmail">
        <Form.Control
          type="email"
          data-testid={AUTH_TEST_IDS.emailInput}
          placeholder={t('Enter email', {forceString: true})}
          autoComplete="email"
          onChange={e => setEmail(e.target.value)}
          required
          isInvalid={error == 'auth/email-already-in-use'}
          aria-label={t('Enter email', {forceString: true})}
        />
        <Form.Control.Feedback type="invalid">
          {t('This email is already in use.')}
        </Form.Control.Feedback>
      </Form.Group>
      <Form.Group className="mb-3" controlId="formBasicPassword">
        <Form.Control
          type="password"
          data-testid={AUTH_TEST_IDS.passwordInput}
          placeholder={t('Enter password', {forceString: true})}
          autoComplete={showSignIn ? 'current-password' : 'new-password'}
          onChange={e => setPassword(e.target.value)}
          isInvalid={errorMessages.includes(error)}
          aria-label={t('Enter password', {forceString: true})}
        />
        <Form.Control.Feedback type="invalid">
          {error === 'invalid-password'
            ? t(
                'Invalid password. Make sure your password is at least 8 characters long.',
              )
            : t(
                'Incorrect login credentials. If you forgot your password or did not set one, click the Forgot Password button below.',
              )}
        </Form.Control.Feedback>
      </Form.Group>
      {showSignIn ? (
        <LogInLinkButton
          data-testid={AUTH_TEST_IDS.forgotPasswordButton}
          onClick={() => setLoginState(LoginScreen.FORGOT_PASWORD)}
          size="sm"
          variant="link"
          className="mb-3"
        >
          {t('Forgot password?')}
        </LogInLinkButton>
      ) : (
        <Form.Group className="mb-3" controlId="formPasswordVerification">
          <Form.Control
            type="password"
            data-testid={AUTH_TEST_IDS.passwordConfirmationInput}
            autoComplete="new-password"
            placeholder={t('Re-enter password', {forceString: true})}
            onChange={e => setPasswordMatch(e.target.value)}
            isInvalid={error == 'no-match'}
            aria-label={t('Re-enter password', {forceString: true})}
            required
          />
          <Form.Control.Feedback type="invalid">
            {t("Passwords don't match.")}
          </Form.Control.Feedback>
        </Form.Group>
      )}
      <PrimaryLogInButton
        data-testid={AUTH_TEST_IDS.submitButton}
        variant="primary"
        type="submit"
        style={{width: '100%'}}
      >
        {showSignIn ? t('Sign In') : t('Create Account')}
      </PrimaryLogInButton>
      <SignInAlert message={connectionAdvice} />
    </Form>
  );
};
